<template>
  <template v-if="mode === 'icon'">
    <div
      class="device-icon-pin"
      :class="[sizeClass, isActive ? 'device-active--climate' : '']"
      :style="{ '--pin-active-color': accentColor, borderColor: isActive ? accentColor : '' }"
      :title="device.deviceName">
      <!-- The drawn steam inside the status ring (P14): the ring carries the
           current temperature over the sauna's own span (the target's own
           40…110 °C bounds), with the numeric reading kept under the icon; a
           device that publishes no reading falls back to the plain on/off arc. -->
      <ControlStatusRing :value="pinRingValue" :accent="accentColor">
        <ControlIcon name="steam" :size="pinIconSize" :style="isActive ? { color: accentColor } : {}" />
        <span
          v-if="currentTemperature !== null"
          class="absolute -bottom-1 rounded-full bg-white/90 px-1.5 py-0.5 text-[10px] font-semibold text-gray-800 shadow dark:bg-gray-900/90 dark:text-gray-100">
          {{ currentTemperature.toFixed(0) }}°
        </span>
      </ControlStatusRing>
      <div v-if="isActive" class="device-icon-pin__glow"></div>
    </div>
  </template>

  <template v-else-if="mode === 'grid'">
    <template v-if="(colSpan ?? 1) >= 2">
      <div class="w-full h-full flex flex-col justify-between rounded-xl border p-2 select-none" :style="gridStyle">
        <div class="flex items-center gap-1.5 min-w-0 cursor-pointer" @click="emit('open-control-modal')">
          <span
            class="material-symbols-outlined text-3xl"
            :style="{ color: isActive ? accentColor : 'rgb(156 163 175)' }">
            {{ defaultIcon }}
          </span>
          <span class="text-base font-semibold text-white truncate flex-1">{{ displayName }}</span>
          <span v-if="currentTemperature !== null" class="text-sm text-gray-300 shrink-0"
            >{{ currentTemperature.toFixed(1) }}°</span
          >
        </div>
        <div class="flex items-end justify-between gap-2">
          <div>
            <div class="text-xs text-gray-300">{{ activityLabel }}</div>
            <div class="text-2xl font-bold" :style="{ color: accentColor }">
              {{ targetTemperature !== null ? targetTemperature.toFixed(0) : '--' }}°
            </div>
          </div>
          <div class="text-right text-xs text-gray-300">
            <div v-if="sessionRemaining !== null">
              {{ tt(`${sessionRemaining.toFixed(0)} min left`, `${sessionRemaining.toFixed(0)} min do konca`) }}
            </div>
            <div v-if="heaterKeys.length">
              {{
                tt(
                  `${activeHeaterCount} heaters on`,
                  `${activeHeaterCount} ${slCount(activeHeaterCount, 'grelnik vklopljen', 'grelnika vklopljena', 'grelniki vklopljeni', 'grelnikov vklopljenih')}`,
                )
              }}
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <DeviceGridCell
        :icon="device.icon || defaultIcon"
        :name="displayName"
        :is-on="isActive"
        active-color="orange-400"
        @toggle="emit('open-control-modal')"
        @long-press="emit('open-control-modal')" />
    </template>
  </template>

  <template v-else-if="mode === 'control'">
    <!-- The body of the sheet (the host chrome owns the head and the panel):
         the hero — the steam circle (tap = the same session / on-off write), the
         state word and the reading — then the setpoint with its stepper, the
         mode chips, the heater toggles, the status tile and the meta line. -->
    <div class="sauna-sheet" :style="{ '--sheet-accent': accentColor }">
      <div class="sauna-hero">
        <button
          type="button"
          class="sauna-circle"
          :class="{ 'sauna-circle--active': isActive, 'sauna-circle--busy': controlling }"
          :disabled="controlling"
          :aria-label="primaryToggleLabel"
          @click="togglePrimary">
          <span v-if="controlling" class="sauna-spin" aria-hidden="true"></span>
          <ControlIcon v-else name="steam" size="2.5rem" />
        </button>
        <div class="sauna-state" :class="{ 'sauna-state--active': isActive }">{{ operationModeLabel }}</div>
        <div class="sauna-caption">{{ currentTemperatureLabel }}</div>
      </div>

      <!-- The setpoint: the target big, the stepper the existing write path
           (`adjustTemperature` — clamped to the device's min/max, rounded to its step). -->
      <div class="sauna-target">
        <p class="sauna-target-label">{{ temperatureLabel }}</p>
        <p class="sauna-target-value">
          {{ targetTemperature !== null ? targetTemperature.toFixed(0) : '--' }}<small>°C</small>
        </p>
        <ControlStepper
          :value-text="targetStepperText"
          hold
          :disabled="controlling || targetTemperature === null"
          @step="stepTemperature" />
      </div>

      <!-- The modes this device offers (operation_mode), one chip each. -->
      <div v-if="modeOptions.length" class="sauna-row">
        <div class="sauna-row-head">
          <span class="sauna-row-key">{{ tt('Mode', 'Način') }}</span>
        </div>
        <div class="sauna-chips">
          <ControlChip
            v-for="option in modeOptions"
            :key="String(option.value)"
            :active="String(operationModeValue) === String(option.value)"
            :disabled="controlling"
            @click="setOperationMode(option.value)">
            {{ modeOptionLabel(option) }}
          </ControlChip>
        </div>
      </div>

      <!-- Heaters (heater1…heaterN) — one toggle row each, the same boolean write. -->
      <div v-if="heaterKeys.length" class="sauna-row">
        <div class="sauna-row-head">
          <span class="sauna-row-key">{{ tt('Heaters', 'Grelniki') }}</span>
          <span class="sauna-row-value">
            {{
              tt(
                `${activeHeaterCount} / ${heaterKeys.length} active`,
                `${activeHeaterCount} / ${heaterKeys.length} aktivnih`,
              )
            }}
          </span>
        </div>
        <div class="sauna-heaters">
          <ControlToggleRow
            v-for="heater in heaterKeys"
            :key="heater"
            :model-value="getBooleanValue([heater])"
            :label="formatHeaterLabel(heater)"
            icon="flame"
            :disabled="controlling"
            @update:model-value="toggleBooleanValue(heater)" />
        </div>
      </div>

      <!-- Status values, where this device reports them. -->
      <div v-if="sessionRemaining !== null" class="sauna-tiles">
        <ControlValueTile
          icon="timer"
          :label="tt('Session remaining', 'Preostalo v seji')"
          :value="sessionRemaining.toFixed(0)"
          unit="min" />
      </div>

      <p v-if="lastUpdated" class="sauna-meta">
        <ControlIcon name="clock" size="0.8125rem" />
        {{ tt('Last updated:', 'Zadnja posodobitev:') }} {{ lastUpdated }}
      </p>
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { formatTime } from '../../utils/formatDate';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import ControlChip from '../device-sheet/ControlChip.vue';
import ControlStepper from '../device-sheet/ControlStepper.vue';
import ControlToggleRow from '../device-sheet/ControlToggleRow.vue';
import ControlValueTile from '../device-sheet/ControlValueTile.vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import { readField } from './deviceWidgetTypes';
import { useDeviceControl } from '../../composables/useDeviceControl';
import { useDeviceValues } from '../../composables/useDeviceValues';
import { useDeviceValueOptionsStore } from '../../stores/deviceValueOptions';
import { tt } from '../../utils/consoleText';
import type { Device, DeviceValueWithReferences } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
  colSpan?: number;
  rowSpan?: number;
}

const props = withDefaults(defineProps<Props>(), { mode: 'icon', size: 'md' });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

const { currentValues, initializeValues } = useDeviceValues(props.device.id);
const { controlling, setProperty } = useDeviceControl();
const deviceValueOptionsStore = useDeviceValueOptionsStore();

const fetchedTargetOptions = ref<{ minValue: number | null; maxValue: number | null; step: number | null } | null>(
  null,
);
const fetchedModeOptions = ref<Array<{ value: number | string; label: string }> | null>(null);

// Both of these are read by loadOptions() from the watcher's very first (immediate)
// run, which happens while this setup is still executing — they must therefore be
// initialized above the watch, not further down the file (TDZ otherwise).
const targetValueDef = computed(() => {
  for (const key of ['target_temperature', 'setpoint']) {
    const value = getValueDefinition(key);
    if (value) return value;
  }
  return null;
});

const modeValueDef = computed(() => getValueDefinition('operation_mode'));

watch(
  () => props.device.deviceValues,
  (values) => {
    if (values?.length) {
      initializeValues(values);
      void loadOptions();
    }
  },
  { immediate: true, deep: true },
);

const accentColor = 'rgb(245, 158, 11)';
const defaultIcon = computed(() => props.device.icon || 'sauna');
const displayName = computed(() =>
  props.displayMode === 'full' ? props.device.deviceName : props.device.shortName || props.device.deviceName,
);
const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

/**
 * The pin's ring (P14): the sauna's current temperature over its own span —
 * the target's bounds (TEMP_MIN/TEMP_MAX, the 40…110 °C fallback). A device
 * with no temperature reading falls back to the plain on/off arc.
 */
const pinRingValue = computed(() => {
  const reading = currentTemperature.value;
  if (reading !== null && TEMP_MAX.value > TEMP_MIN.value) {
    const pct = ((reading - TEMP_MIN.value) / (TEMP_MAX.value - TEMP_MIN.value)) * 100;
    return Math.max(0, Math.min(100, pct));
  }
  return isActive.value ? 100 : 0;
});

/** The drawn steam's size inside the pin (P14), in the `GenericIcon` steps. */
const pinIconSize = computed(() => {
  if (props.fluid) return '28px';
  switch (props.size) {
    case 'lg':
      return '28px';
    case 'sm':
      return '16px';
    default:
      return '22px';
  }
});

const lastUpdated = computed(() => (Object.keys(currentValues.value).length > 0 ? formatTime(new Date()) : ''));

function toFiniteNumber(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function hasValueKey(key: string): boolean {
  return Boolean(props.device.deviceValues?.some((value) => value.valueKey === key));
}

function getValueDefinition(key: string): DeviceValueWithReferences | null {
  return props.device.deviceValues?.find((value) => value.valueKey === key) ?? null;
}

function getBooleanValue(keys: string[]): boolean {
  for (const key of keys) {
    if (key in currentValues.value) {
      const value = currentValues.value[key];
      return value === true || value === 1 || value === 'on' || value === 'true';
    }
  }

  const deviceValue = props.device.deviceValues?.find((value) => keys.includes(value.valueKey));
  const raw = deviceValue?.currentValue;
  return raw === true || raw === 1 || raw === 'on' || raw === 'true';
}

function getNumericValue(keys: string[]): number | null {
  for (const key of keys) {
    if (key in currentValues.value) {
      const numeric = toFiniteNumber(currentValues.value[key]);
      if (numeric !== null) return numeric;
    }
  }

  const deviceValue = props.device.deviceValues?.find((value) => keys.includes(value.valueKey));
  return toFiniteNumber(deviceValue?.currentValue);
}

const primaryToggleKey = computed(() => {
  for (const key of ['session_active', 'on_off', 'power', 'switch', 'state']) {
    if (hasValueKey(key)) return key;
  }
  return 'on_off';
});

const targetValueKey = computed(() => targetValueDef.value?.valueKey || 'target_temperature');
const currentTemperature = computed(() => getNumericValue(['current_temperature', 'temperature']));
const targetTemperature = computed(() => getNumericValue([targetValueKey.value, 'target_temperature', 'setpoint']));
const sessionRemaining = computed(() => getNumericValue(['session_remaining']));
const operationModeValue = computed(
  () => currentValues.value['operation_mode'] ?? modeValueDef.value?.currentValue ?? null,
);

const heaterKeys = computed(() =>
  (props.device.deviceValues ?? [])
    .map((value) => String(value.valueKey || ''))
    .filter((valueKey) => /^heater\d+$/.test(valueKey))
    .sort((left, right) => Number(left.replace('heater', '')) - Number(right.replace('heater', ''))),
);

const activeHeaterCount = computed(() => heaterKeys.value.filter((heater) => getBooleanValue([heater])).length);
const isActive = computed(() => getBooleanValue([primaryToggleKey.value]));
const currentTemperatureLabel = computed(() =>
  currentTemperature.value !== null
    ? tt(`${currentTemperature.value.toFixed(1)}°C current`, `${currentTemperature.value.toFixed(1)}°C trenutno`)
    : tt('Temperature unavailable', 'Temperatura ni na voljo'),
);
const primaryToggleLabel = computed(() => {
  if (primaryToggleKey.value === 'session_active') {
    return isActive.value ? tt('Stop Session', 'Končaj sejo') : tt('Start Session', 'Začni sejo');
  }
  return isActive.value ? tt('Turn Off', 'Izklopi') : tt('Turn On', 'Vklopi');
});
const temperatureLabel = computed(() =>
  primaryToggleKey.value === 'session_active'
    ? tt('Session Temperature', 'Temperatura seje')
    : tt('Target Temperature', 'Ciljna temperatura'),
);

/** The stepper's reading — the same number the hero shows, with its unit. */
const targetStepperText = computed(() =>
  targetTemperature.value !== null ? `${targetTemperature.value.toFixed(0)} °C` : '--',
);

/**
 * The four modes this widget knows by itself, in the app's own pair shape
 * (`label: ['English', 'Slovensko']`). A hub that sends its own enum labels
 * (`fetchedModeOptions`) is left exactly as it comes — those words are its data,
 * not this build's copy.
 */
const fallbackModeOptions: Array<{ value: number; label: [string, string] }> = [
  { value: 0, label: ['Off', 'Izklopljeno'] },
  { value: 1, label: ['Preheat', 'Predgrevanje'] },
  { value: 2, label: ['Session', 'Seja'] },
  { value: 3, label: ['Eco', 'Eko'] },
];

/** A mode option's label in the reader's language — this file's pair, or the hub's own word. */
function modeOptionLabel(option: { label: string | [string, string] }): string {
  return Array.isArray(option.label) ? tt(option.label[0], option.label[1]) : option.label;
}

const modeOptions = computed(() =>
  fetchedModeOptions.value?.length ? fetchedModeOptions.value : modeValueDef.value ? fallbackModeOptions : [],
);

const operationModeLabel = computed(() => {
  const option = modeOptions.value.find((item) => String(item.value) === String(operationModeValue.value));
  if (option) return modeOptionLabel(option);
  if (primaryToggleKey.value === 'session_active') {
    return isActive.value ? tt('Session Active', 'Seja aktivna') : tt('Standby', 'Pripravljeno');
  }
  return isActive.value ? tt('Heating', 'Gretje') : tt('Off', 'Izklopljeno');
});

const activityLabel = computed(() => {
  if (sessionRemaining.value !== null && isActive.value) {
    return tt(`${sessionRemaining.value.toFixed(0)} min left`, `${sessionRemaining.value.toFixed(0)} min do konca`);
  }
  return operationModeLabel.value;
});

function getTargetMetaNumber(keys: string[], fallback: number): number {
  for (const source of [fetchedTargetOptions.value, targetValueDef.value]) {
    if (!source) continue;
    for (const key of keys) {
      const numeric = toFiniteNumber(readField(source, key));
      if (numeric !== null) return numeric;
    }
  }
  return fallback;
}

const TEMP_MIN = computed(() => getTargetMetaNumber(['minValue', 'min_value', 'min'], 40));
const TEMP_MAX = computed(() => getTargetMetaNumber(['maxValue', 'max_value', 'max'], 110));
const tempStep = computed(() => {
  const step = getTargetMetaNumber(['step', 'deltaValue', 'delta_value'], 1);
  return step > 0 ? step : 1;
});

function roundToStep(value: number, step: number): number {
  const precision = String(step).split('.')[1]?.length ?? 0;
  return Number((Math.round(value / step) * step).toFixed(precision));
}

const gridStyle = computed(() => ({
  background: isActive.value ? `${accentColor}18` : 'rgba(255,255,255,0.05)',
  borderColor: isActive.value ? `${accentColor}55` : 'rgba(255,255,255,0.12)',
}));

function formatHeaterLabel(key: string) {
  return tt(`Heater ${key.replace('heater', '')}`, `Grelnik ${key.replace('heater', '')}`);
}

/**
 * Slovene agrees with the count (1 grelnik, 2 grelnika, 3 grelniki, 5
 * grelnikov); the last two digits decide, as the language does. The same small
 * helper the drawers and the scene card carry.
 */
function slCount(n: number, one: string, two: string, few: string, many: string): string {
  const lastTwo = n % 100;
  if (lastTwo === 1) return one;
  if (lastTwo === 2) return two;
  if (lastTwo === 3 || lastTwo === 4) return few;
  return many;
}

async function loadOptions() {
  if (targetValueDef.value?.id) {
    const options = await deviceValueOptionsStore.fetchOptions(targetValueDef.value.id);
    fetchedTargetOptions.value = {
      minValue: toFiniteNumber(options?.min_value ?? options?.minValue),
      maxValue: toFiniteNumber(options?.max_value ?? options?.maxValue),
      step: toFiniteNumber(options?.step ?? options?.delta_value ?? options?.deltaValue),
    };
  }

  if (modeValueDef.value?.id) {
    const options = await deviceValueOptionsStore.fetchOptions(modeValueDef.value.id);
    fetchedModeOptions.value = Array.isArray(options?.enum_options)
      ? options.enum_options.map((item) => ({ value: item.value as number | string, label: item.label }))
      : null;
  }
}

async function togglePrimary() {
  await setProperty(props.device, primaryToggleKey.value, !isActive.value);
}

async function adjustTemperature(delta: number) {
  const current = targetTemperature.value ?? TEMP_MIN.value;
  const next = Math.max(TEMP_MIN.value, Math.min(TEMP_MAX.value, roundToStep(current + delta, tempStep.value)));
  await setProperty(props.device, targetValueKey.value, next);
}

async function stepTemperature(delta: 1 | -1) {
  await adjustTemperature(delta * tempStep.value);
}

async function setOperationMode(value: number | string) {
  await setProperty(props.device, 'operation_mode', value);
}

async function toggleBooleanValue(key: string) {
  await setProperty(props.device, key, !getBooleanValue([key]));
}
</script>

<style scoped>
/* The tiles' own layout: the head at the top, the chips along the bottom, so a
   sauna reads like the thermostat and the ventilation tiles beside it. */
.sauna-button-layout {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 0.4em;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 0;
  /* Space for the menu button and the slider */
  padding-right: 1em;
  text-align: left;
}

.sauna-button-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25em;
}

/* Chips size off the tile's own font size, so they scale with the tile */
.sauna-button-chip {
  border-radius: 0.5em;
  padding: 0.15em 0.5em;
  font-size: 0.55em;
  font-weight: 500;
  line-height: 1.1;
  white-space: nowrap;
}

/* The room's own reading rides beside the setpoint: smaller and quieter */
.sauna-button-current {
  margin-left: 0.4em;
  font-size: 0.68em;
  font-weight: 600;
  color: rgb(107 114 128);
}

.dark .sauna-button-current {
  color: rgb(156 163 175);
}
</style>

<style scoped>
/* Control mode: the sheet body (the host chrome owns the head and the panel).
   Every colour reads `--sheet-accent`, which the body root sets to the
   sauna's own amber. */
.sauna-sheet {
  display: flex;
  flex-direction: column;
  gap: calc(1rem * var(--ui-space-factor));
}

.sauna-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
}

/* The one big control: a 6rem circle, tinted by the device's accent while the
   session is on; the busy state calms it while a write is in flight. */
.sauna-circle {
  width: 6rem;
  height: 6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--glass-edge);
  border-radius: 50%;
  background: rgba(148, 163, 184, 0.1);
  color: rgb(var(--gray-500));
  cursor: pointer;
  transition:
    color 0.2s,
    border-color 0.2s,
    background 0.2s,
    box-shadow 0.2s,
    transform 0.15s;
}

.dark .sauna-circle {
  background: rgba(255, 255, 255, 0.04);
  color: rgb(var(--gray-400));
}

.sauna-circle:hover:not(:disabled) {
  transform: translateY(-1px);
}

.sauna-circle--active {
  border-color: color-mix(in srgb, var(--sheet-accent) 55%, transparent);
  background: color-mix(in srgb, var(--sheet-accent) 13%, transparent);
  color: var(--sheet-accent);
  box-shadow: 0 0 24px color-mix(in srgb, var(--sheet-accent) 22%, transparent);
}

.sauna-circle--busy {
  opacity: 0.7;
}

.sauna-circle:disabled {
  cursor: default;
}

.sauna-spin {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  border: 2px solid color-mix(in srgb, var(--sheet-accent) 35%, transparent);
  border-top-color: var(--sheet-accent);
  animation: sauna-spin 0.8s linear infinite;
}

@media (prefers-reduced-motion: reduce) {
  .sauna-spin {
    animation-duration: 2.4s;
  }
}

@keyframes sauna-spin {
  to {
    transform: rotate(360deg);
  }
}

.sauna-state {
  font-size: 1.0625rem;
  font-weight: 700;
  color: rgb(var(--gray-600));
}

.dark .sauna-state {
  color: rgb(var(--gray-200));
}

.sauna-state--active {
  color: var(--sheet-accent);
}

.sauna-caption {
  font-size: 0.75rem;
  color: rgb(var(--gray-500));
}

.dark .sauna-caption {
  color: rgb(var(--gray-400));
}

/* The setpoint block: the label, the big target, the stepper. */
.sauna-target {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: calc(0.875rem * var(--ui-space-factor));
  border: 1px solid var(--glass-edge);
  border-radius: calc(0.75rem * var(--ui-radius-factor));
  background: rgba(148, 163, 184, 0.1);
}

.dark .sauna-target {
  background: rgba(255, 255, 255, 0.04);
}

.sauna-target-label {
  margin: 0;
  font-size: 0.6875rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgb(var(--gray-500));
}

.dark .sauna-target-label {
  color: rgb(var(--gray-400));
}

.sauna-target-value {
  margin: 0;
  font-size: 2.25rem;
  font-weight: 700;
  line-height: 1.1;
  color: var(--sheet-accent);
  font-variant-numeric: tabular-nums;
}

.sauna-target-value small {
  margin-left: 0.15rem;
  font-size: 1.125rem;
  font-weight: 600;
}

/* Section rows: a key line over the controls. */
.sauna-row {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}

.sauna-row-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
}

.sauna-row-key {
  font-size: 0.6875rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgb(var(--gray-500));
}

.dark .sauna-row-key {
  color: rgb(var(--gray-400));
}

.sauna-row-value {
  font-size: 0.75rem;
  font-weight: 600;
  color: rgb(var(--gray-600));
  font-variant-numeric: tabular-nums;
}

.dark .sauna-row-value {
  color: rgb(var(--gray-300));
}

.sauna-chips {
  display: flex;
  flex-wrap: wrap;
  gap: calc(0.375rem * var(--ui-space-factor));
}

.sauna-heaters {
  display: flex;
  flex-direction: column;
}

.sauna-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
  gap: calc(0.5rem * var(--ui-space-factor));
}

.sauna-meta {
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375em;
  font-size: 0.75rem;
  color: rgb(var(--gray-500));
}

.dark .sauna-meta {
  color: rgb(var(--gray-400));
}
</style>
